import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api';
import { format, isToday, isFuture, parseISO } from 'date-fns';

const Dashboard = () => {
  const { user } = useAuth();
  const [balance, setBalance] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const year = new Date().getFullYear();

  useEffect(() => {
    const fetch = async () => {
      try {
        const [balRes, reqRes] = await Promise.all([
          api.get(`/api/holidays/my/balance?year=${year}`),
          api.get(`/api/holidays/my?year=${year}`)
        ]);
        setBalance(balRes.data);
        setRequests(reqRes.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [year]);

  const typeLabels = { holiday: 'Holiday', sick: 'Sick Day', awol: 'Absent Without Leave' };
  const upcoming = requests.filter(r => r.status === 'approved' && isFuture(new Date(r.start_date.substring(0,10) + 'T00:00:00')));
  const pending = requests.filter(r => r.status === 'pending');

  const today = format(new Date(), 'EEEE, d MMMM yyyy');
  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  if (loading) return <div className="loader">Loading...</div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <p style={{ color: 'var(--grey-mid)', fontSize: '13px', marginBottom: '4px' }}>{today}</p>
          <h1 className="page-title">{greeting()}, {user?.name?.split(' ')[0]}</h1>
        </div>
        <Link to="/request" className="btn btn-primary">+ Request Absence</Link>
      </div>

      {/* Balance cards */}
      {balance && user?.holiday_entitlement > 0 && (
        <div className="stat-grid">
          <div className="stat-card gold-accent">
            <div className="stat-label">Entitlement</div>
            <div className="stat-value">{balance.entitlement}</div>
            <div className="stat-sub">days this year</div>
          </div>
          <div className="stat-card navy-accent">
            <div className="stat-label">Remaining</div>
            <div className="stat-value">{balance.remaining}</div>
            <div className="stat-sub">days available</div>
          </div>
          <div className="stat-card success-accent">
            <div className="stat-label">Taken</div>
            <div className="stat-value">{balance.used}</div>
            <div className="stat-sub">days approved</div>
          </div>
          <div className="stat-card">
            <div className="stat-label">Pending</div>
            <div className="stat-value">{balance.pending}</div>
            <div className="stat-sub">days awaiting approval</div>
          </div>
        </div>
      )}

      {balance && user?.holiday_entitlement === 0 && (
        <div className="alert alert-info" style={{ marginBottom: '28px' }}>
          You are not allocated a holiday entitlement. You can still log sick days and unauthorised absences.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
        {/* Upcoming approved */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Upcoming</h3>
            <Link to="/my-holidays" style={{ fontSize: '12px', color: 'var(--gold)', textDecoration: 'none', letterSpacing: '0.5px' }}>View all</Link>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {upcoming.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: 'var(--grey-mid)', fontSize: '13px' }}>
                No upcoming approved absences
              </div>
            ) : (
              upcoming.slice(0, 4).map(r => (
                <div key={r.id} style={{ padding: '14px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '13px', color: 'var(--navy)', fontWeight: '500' }}>
                      {format(new Date(r.start_date.substring(0,10) + 'T00:00:00'), 'd MMM')} {r.start_date !== r.end_date ? `- ${format(new Date(r.end_date.substring(0,10) + 'T00:00:00'), 'd MMM')}` : ''}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--grey-mid)', marginTop: '2px' }}>{r.days_taken} day{r.days_taken !== 1 ? 's' : ''}</div>
                  </div>
                  <span className={`badge badge-${r.absence_type}`}>{typeLabels[r.absence_type]}</span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Pending requests */}
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Pending Approval</h3>
            <span style={{ fontSize: '12px', color: 'var(--grey-mid)' }}>{pending.length} request{pending.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {pending.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center', color: 'var(--grey-mid)', fontSize: '13px' }}>
                No pending requests
              </div>
            ) : (
              pending.map(r => (
                <div key={r.id} style={{ padding: '14px 24px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: '13px', color: 'var(--navy)', fontWeight: '500' }}>
                      {format(new Date(r.start_date.substring(0,10) + 'T00:00:00'), 'd MMM')} {r.start_date !== r.end_date ? `- ${format(new Date(r.end_date.substring(0,10) + 'T00:00:00'), 'd MMM')}` : ''}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--grey-mid)', marginTop: '2px' }}>{typeLabels[r.absence_type]} - {r.days_taken} day{r.days_taken !== 1 ? 's' : ''}</div>
                  </div>
                  <span className="badge badge-pending">Pending</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
